import { AuthService } from './shared/services/auth.service';
import { AuthCallbackComponent } from './auth-callback/auth-callback.component';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LayoutModule } from '@angular/cdk/layout';
import {
  PreloadAllModules,
  Router,
  RouterModule,
  Routes,
  Event,
  NavigationStart,
  NavigationEnd,
  NavigationError,
} from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { CUSTOM_ELEMENTS_SCHEMA, Injector, NgModule } from '@angular/core';
import { FuseSharedModule } from '@fuse/shared.module';
import { AppComponent } from './app.component';
import { fuseConfig } from './fuse-config';
import { FuseMainModule } from './main/main.module';
import { SharedModule } from './shared/shared.module';
import {
  ALLOW_LAST_LOGIN,
  APPCOPYRIGHT,
  APPNAME,
  APPVERSION,
  BASE_PATH,
  DEBUG_FLAG,
  IDENTITY_URL,
  POPUP_LOGIN,
} from './app.GlobalSettings';
import { ReportingModule } from './main/content/reporting/reporting.module';
import {
  HTTP_INTERCEPTORS,
  HttpClientModule,
  HttpClient,
  HttpClientJsonpModule,
} from '@angular/common/http';
import { FuseModule } from '@fuse/fuse.module';
import { AuthInjectorService } from './shared/services/auth-injector.service';
import { AuthGuardService } from './shared/services/auth-guard.service';
import { MessageBoxComponent } from './components/messageBox/messageBox.component';
import { AutoLogoutWarningMsgBoxComponent } from './components/autoLogoutWarningMsgBox/autoLogoutWarningMsgBox.component';
import { DefaultDataService } from './shared/services/default-data.service';
import { DateModule } from './main/content/misc/date.module';
import { ToastrModule } from 'ngx-toastr';
import { PhoenixPipesModule } from './pipes/pipes';
// import { MonitoringService } from './shared/services/monitoring/monitoring.service';
import { BaseComponent } from './main/content/base/base.component';
import { FuseTaskModule } from './main/content/tasks/task.module';
import { CustomHttpInterceptorService } from './shared/services/cache-control-injector.service';
import { SearchService } from './shared/services/search.service';
import { SelectivePreloadingStrategyService } from './shared/services/selective-preloading-strategy.service';
import { FeedbackComponent } from './components/dlgFeedback/dlgFeedback.component';
import { PhoenixDirectivesModule } from './directives/directives.module';
import { LogoutModule } from './main/content/misc/logout.module';
import { EventService } from './shared/services/event.service';
import { SelectDateComponent } from './components/selectDate/selectDate.component';
import { IntecDatePickerModule } from './components/intec-date-picker2/intec-date-picker.module';
import { ValidationErrorsModule } from './components/validation-errors/validation-errors.module';
import { AuditService } from './shared/services/audit.service';
import { LoadingHttpInterceptorService } from './shared/services/loadingHttpInterceptor.service';
import {
  DEFAULT_TIMEOUT,
  TimeoutInterceptorService,
} from './shared/services/timeoutInterceptor.service';
import { ReferralsModule } from './main/content/referrals/referrals.module';
import { sendRequestFactory } from './shared/helpers/devex-http-helper';
import devextremeAjax from 'devextreme/core/utils/ajax';
import config from 'devextreme/core/config';
import { SubjectStopwatchModule } from './shared/services/subject-stopwatch/subject-stopwatch.module';
import {
  DxCheckBoxModule,
  DxHtmlEditorModule,
  DxSelectBoxModule,
} from 'devextreme-angular';
import { DxiItemModule, DxoToolbarModule } from 'devextreme-angular/ui/nested';
import { DxFileUploaderModule } from 'devextreme-angular';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from 'environments/environment';

const appRoutes: Routes = [
  {
    path: 'auth-callback',
    component: AuthCallbackComponent,
  },
  {
    path: 'mobile',
    loadChildren: () =>
      import('./mobile/mobile.module').then((m) => m.MobileModule),
  },
  {
    path: 'landing',
    loadChildren: () =>
      import('./main/content/landing/landing.module').then(
        (m) => m.LandingModule
      ),
  },
  {
    path: 'subjects',
    loadChildren: () =>
      import('./main/content/subjects/subjects.module').then(
        (m) => m.SubjectsModule
      ),
    canActivate: [AuthGuardService],
    data: { auth: 'USER,READONLY,ORGMANAGER,MULTIORGADMIN' },
  },
  {
    path: 'processes',
    loadChildren: () =>
      import('./main/content/processes/processes.module').then(
        (m) => m.ProcessesModule
      ),
    canActivate: [AuthGuardService],
    data: { auth: 'USER,READONLY,ORGMANAGER,MULTIORGADMIN' },
  },
  {
    path: 'dashboard',
    loadChildren: () =>
      import('./main/content/dashboard/dashboard.module').then(
        (m) => m.DashboardModule
      ),
    canActivate: [AuthGuardService],
    data: { auth: 'USER,READONLY,ORGMANAGER,MULTIORGADMIN' },
  },
  {
    path: 'referrals',
    loadChildren: () =>
      import('./main/content/referrals/referrals.module').then(
        (m) => m.ReferralsModule
      ),
    canActivate: [AuthGuardService],
    data: { auth: 'USER,READONLY,ORGMANAGER,MULTIORGADMIN' },
  },
  {
    path: 'searchresults',
    loadChildren: () =>
      import('./main/content/searches/searches.module').then(
        (m) => m.SearchesModule
      ),
    canActivate: [AuthGuardService],
    data: { auth: 'USER,READONLY,ORGMANAGER,MULTIORGADMIN' },
  },
  {
    path: 'system-maintenance',
    loadChildren: () =>
      import(
        './main/content/system-maintenance/system-maintenance.module'
      ).then((m) => m.SystemMaintenanceModule),
    canActivate: [AuthGuardService],
    data: { auth: 'ADMIN,ORGMANAGER', noPreload: true },
  },
  {
    path: 'tasks',
    loadChildren: () =>
      import('./main/content/tasks/task.module').then((m) => m.FuseTaskModule),
    canActivate: [AuthGuardService],
    data: { auth: 'USER,READONLY,ORGMANAGER,MULTIORGADMIN' },
  },
  {
    path: 'notes',
    loadChildren: () =>
      import('./main/content/notes/notes.module').then((m) => m.NotesModule),
    canActivate: [AuthGuardService],
    data: { auth: 'USER,READONLY,ORGMANAGER,MULTIORGADMIN' },
  },
  {
    path: 'marker-search',
    loadChildren: () =>
      import('./main/content/websearch/web-search.module').then(
        (m) => m.WebSearchModule
      ),
    canActivate: [AuthGuardService],
    data: { auth: 'ADMIN,USER,SEARCHONLY,ORGMANAGER,MULTIORGADMIN', noPreload: true },
  },
  {
    path: 'import',
    loadChildren: () =>
      import('./main/content/import/import.module').then((m) => m.ImportModule),
    canActivate: [AuthGuardService],
    data: { auth: 'ADMIN,USER,ORGMANAGER,SUBJECTALERTIMPORT', noPreload: true },
  },
  {
    path: 'intec-admin',
    loadChildren: () =>
      import('./main/content/intec-admin/intec-admin.module').then(
        (m) => m.IntecAdminModule
      ),
    canActivate: [AuthGuardService],
    data: { auth: 'SYSADMIN,ORGMANAGER', noPreload: true },
  },
  {
    path: '**',
    redirectTo: '/landing',
    pathMatch: 'full',
  },
];

@NgModule({
  declarations: [
    AppComponent,
    AuthCallbackComponent,
    MessageBoxComponent,
    SelectDateComponent,
    AutoLogoutWarningMsgBoxComponent,
    FeedbackComponent,
    BaseComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    LayoutModule,
    HttpClientJsonpModule,
    RouterModule.forRoot(appRoutes, {
      preloadingStrategy: SelectivePreloadingStrategyService,
      enableTracing: true,
    }),
    DxHtmlEditorModule,
    DxCheckBoxModule,
    DxSelectBoxModule,
    DxoToolbarModule,
    DxiItemModule,
    DxFileUploaderModule,
    // Fuse Main and Shared modules
    FuseModule.forRoot(fuseConfig),
    FuseSharedModule,
    FuseMainModule,
    SharedModule.forRoot(),
    SubjectStopwatchModule,
    MatButtonModule,
    MatDialogModule,
    MatDividerModule,
    MatInputModule,
    MatIconModule,
    MatSortModule,
    MatTableModule,
    MatToolbarModule,
    MatPaginatorModule,
    MatNativeDateModule,
    MatTooltipModule,
    ReportingModule,
    FuseTaskModule,
    ReferralsModule,
    // used for setting system wide dateformats for date pickers
    DateModule,
    LogoutModule,
    PhoenixPipesModule.forRoot(),
    PhoenixDirectivesModule,
    IntecDatePickerModule,
    ValidationErrorsModule,
    ToastrModule.forRoot({
      timeOut: 2000,
      progressBar: true,
      newestOnTop: true,
      positionClass: 'toast-bottom-right',
    }), // ToastrModule added
  ],
  exports: [
    MessageBoxComponent,
    SelectDateComponent,
    AutoLogoutWarningMsgBoxComponent,
    FeedbackComponent,
    BaseComponent,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    AuditService,
    AuthService,
    AuthGuardService,
    AuthInjectorService,
    // MonitoringService,
    DefaultDataService,
    SearchService,
    EventService,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInjectorService, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomHttpInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingHttpInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TimeoutInterceptorService,
      multi: true,
    },
    { provide: DEFAULT_TIMEOUT, useValue: 60000 },
    {
      provide: BASE_PATH,
      useFactory: () => {
        var url = getAppRootAttributeFactory(
          'BasePath',
          'https://' + window.location.host
        );
        return url;
      },
    },
    {
      provide: IDENTITY_URL,
      useFactory: () => {
        // It is important to use correct value case CORS will block incorrect URLs
        var url = '';
        if (environment.production) {
          if (
            window.location.host
              .toLocaleLowerCase()
              .endsWith('tflincasetest.azurewebsites.net')
          )
            // Azure Test
            url = getAppRootAttributeFactory(
              'IdentityProviderURL',
              'https://intecid.azurewebsites.net'
            );
          // Azure Live
          else
            url = getAppRootAttributeFactory(
              'IdentityProviderURL',
              'https://phoenix365identity.azurewebsites.net'
            );
        } else {
          // Local Dev
          url = getAppRootAttributeFactory(
            'IdentityProviderURL',
            'https://ics.intecdev.co.uk:5000'
          );
        }
        return url;
      },
    },
    {
      provide: POPUP_LOGIN,
      useFactory: () =>
        getAppRootAttributeFactory('PopupLoginPrompts', '1') !== '0',
    },
    {
      provide: DEBUG_FLAG,
      useFactory: () => getAppRootAttributeFactory('Debug', '0') !== '0',
    },
    {
      provide: APPNAME,
      useFactory: () => getAppRootAttributeFactory('AppName', 'TFL InCase 365'),
    },
    {
      provide: APPVERSION,
      useFactory: () => getAppRootAttributeFactory('AppVersion', '0.1'),
    },
    {
      provide: APPCOPYRIGHT,
      useFactory: () => getAppRootAttributeFactory('Copyright', '0.1'),
    },
    {
      provide: ALLOW_LAST_LOGIN,
      useFactory: () =>
        getAppRootAttributeFactory('AllowLastLogin', '0') === '1',
    },
  ],
})
export class AppModule {
  // get an injector instance and store in the global variable AppInjector
  currentRoute: string;

  constructor(
    private injector: Injector,
    httpClient: HttpClient,
    router: Router
  ) {
    AppInjector = this.injector;
    // This code will allow devextreme data source classes to use Angular httpClient. This means we can use the same http interceptor
    // to inject auth bearer token into the http request header, and check the result for 401 and redirect to login
    devextremeAjax.inject({ sendRequest: sendRequestFactory(httpClient) });

    config({
      defaultCurrency: 'GBP',
    });

    this.currentRoute = '';
    router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        // Show progress spinner or progress bar
        console.log('Route change started', event);
      }

      if (event instanceof NavigationEnd) {
        // Hide progress spinner or progress bar
        this.currentRoute = event.url;
        console.log('Route change end', event);
      }

      if (event instanceof NavigationError) {
        // Hide progress spinner or progress bar
        // Present error to user
        console.log('Navigation Error', event);
      }
    });
  }
}

// global variable to store an injector instance
// can then manually inject an instance if needed using myVar = AppInjector.get(requiredClass);
export let AppInjector: Injector;

// This code will try to find the App-Root tag in the html page, and then it will look for the specified attribute, and then return that value
// else it will return the default string.
// This is used to pull config data from the page which used to configure other startup settings so the API and Identity services can be access
// This allow allows us to inject the correct config into the index.html file with values from the ASP.NET Core appSettings.json file at run time.
export function getAppRootAttributeFactory(
  attributeName: string,
  defaultValue
) {
  const el = document.getElementsByTagName('app-root')[0];
  if (el != null) {
    if (el.hasAttribute(attributeName)) {
      const valueString = el.getAttribute(attributeName);
      console.log('app-root attribute ' + attributeName, valueString);
      return valueString;
    }
  }
  return defaultValue;
}
